const tableauPowerBICheatsheet = {
  id: "tableau-powerbi",
  title: "Tableau & Power BI",
  level: "All Levels",
  description:
    "Data visualization techniques and best practices for Tableau and Power BI with interactive examples",
  language: "tableau",
  category: "Data Visualization",
  sections: [
    {
      title: "Getting Started",
      icon: "📊",
      items: [
        {
          title: "Basic Concepts",
          content:
            "Basic principles of data visualization and dashboard design.",
          code: `# Common Data Visualization Principles
- Use appropriate chart types
- Maintain consistent color schemes
- Include clear labels and titles
- Ensure proper data formatting
- Add interactive elements

# Dashboard Design
- Keep it simple and focused
- Use consistent layout
- Include filters and parameters
- Add clear navigation
- Optimize for different devices`,
          note: "When starting a new visualization project, always consider these fundamental principles.",
        },
      ],
    },
    {
      title: "Tableau Essentials",
      icon: "🔍",
      items: [
        {
          title: "Calculated Fields",
          content:
            "Essential Tableau calculations for data analysis and visualization.",
          code: `// Basic Calculations
[Sales] * [Quantity]  // Multiplication
SUM([Profit]) / SUM([Sales])  // Division
IF [Category] = "Furniture" THEN [Sales] * 1.1 ELSE [Sales] END  // Conditional

// Table Calculations
WINDOW_AVG(SUM([Sales]), -2, 2)  // Moving Average
RANK(SUM([Sales]), 'desc')  // Ranking
RUNNING_SUM(SUM([Sales]))  // Running Total

// Level of Detail (LOD) Expressions
{FIXED [Region] : SUM([Sales])}  // Fixed LOD
{INCLUDE [Category] : AVG([Profit])}  // Include LOD
{EXCLUDE [Month] : SUM([Quantity])}  // Exclude LOD`,
          note: "Use calculated fields to create custom metrics and advanced analytics.",
        },
        {
          title: "Parameters & Filters",
          content:
            "Creating and using parameters and filters for interactive dashboards.",
          code: `// Parameter Creation
[Date Range]  // Date parameter
[Category Selection]  // String parameter
[Threshold Value]  // Numeric parameter

// Filter Applications
[Sales] > [Threshold Value]  // Filter by measure
[Region] IN [Region Selection]  // Filter by dimension
DATEDIFF('day', [Order Date], TODAY()) <= [Date Range]  // Date filter

// Advanced Filtering
CASE [Parameter Selection]
    WHEN "Top N" THEN [Rank] <= [N Value]
    WHEN "Bottom N" THEN [Rank] >= COUNTD([Product]) - [N Value] + 1
    ELSE TRUE
END`,
          note: "Use parameters and filters to create dynamic and user-interactive dashboards.",
        },
      ],
    },
    {
      title: "Power BI Features",
      icon: "💡",
      items: [
        {
          title: "DAX Formulas",
          content:
            "Essential DAX formulas for Power BI calculations and analysis.",
          code: `// Basic DAX
Total Sales = SUM(Sales[Amount])
Average Price = AVERAGE(Sales[Price])
Profit Margin = DIVIDE([Profit], [Sales], 0)

// Time Intelligence
YTD Sales = TOTALYTD([Total Sales], 'Date'[Date])
QTD Sales = TOTALQTD([Total Sales], 'Date'[Date])
MTD Sales = TOTALMTD([Total Sales], 'Date'[Date])

// Advanced DAX
Running Total = 
CALCULATE(
    [Total Sales],
    FILTER(
        ALLSELECTED('Date'[Date]),
        'Date'[Date] <= MAX('Date'[Date])
    )
)

Market Share = 
DIVIDE(
    [Total Sales],
    CALCULATE(
        [Total Sales],
        ALL(Sales[Product])
    ),
    0
)`,
          note: "Use DAX to create complex calculations and business metrics.",
        },
        {
          title: "Power Query M",
          content:
            "Power Query M language for data transformation and preparation.",
          code: `// Basic Transformations
= Table.TransformColumns(#"Previous Step", {{"Column1", Text.Proper, type text}})
= Table.ReplaceValue(#"Previous Step", "old", "new", Replacer.ReplaceText, {"Column1"})

// Conditional Logic
= Table.AddColumn(#"Previous Step", "Status", each if [Value] > 100 then "High" else "Low")

// Advanced Transformations
= Table.Group(#"Previous Step", {"Category"}, {{"Total", each List.Sum([Amount]), type number}})
= Table.Sort(#"Previous Step", {{"Date", Order.Ascending}, {"Amount", Order.Descending}})`,
          note: "Use Power Query to clean, transform, and prepare your data for analysis.",
        },
      ],
    },
    {
      title: "Advanced Visualization",
      icon: "🎨",
      items: [
        {
          title: "Custom Visualizations",
          content:
            "Advanced visualization techniques for both Tableau and Power BI.",
          code: `# Tableau Customization
- Use dual-axis charts for comparison
- Implement small multiples
- Create custom shapes and icons
- Use parameter controls
- Implement advanced tooltips

# Power BI Custom Visuals
- Use custom visuals from marketplace
- Implement R or Python visuals
- Create custom themes
- Use bookmarks for storytelling
- Implement drill-through functionality`,
          note: "Use advanced visualization techniques to create more impactful and insightful dashboards.",
        },
        {
          title: "Performance Optimization",
          content:
            "Techniques for optimizing dashboard and data model performance.",
          code: `# Data Model Optimization
- Use appropriate data types
- Implement proper relationships
- Create calculated columns wisely
- Use aggregations
- Implement partitioning

# Visualization Performance
- Limit number of marks
- Use extracts instead of live connections
- Implement data source filters
- Use context filters
- Optimize calculations`,
          note: "Use these optimization techniques to improve dashboard performance and user experience.",
        },
      ],
    },
  ],
};

export default tableauPowerBICheatsheet;
